// Client-side pre-parser. Runs in the browser to keep the edge function light.
// - CSV / XLSX: parsed into a header + rows table.
// - Plain text: sent as text.
// - PDF / DOCX / images / unknown: skipped here; server fetches from storage
//   and runs Gemini multimodal extraction.

import * as XLSX from "xlsx";
import Papa from "papaparse";
import type { ClientParseResult } from "./types";

const AI_ONLY_MIME = /(pdf|image|word|officedocument\.wordprocessingml)/i;
const AI_ONLY_EXT = /\.(pdf|docx?|png|jpe?g|webp)$/i;

export function needsServerParse(file: File): boolean {
  return AI_ONLY_MIME.test(file.type) || AI_ONLY_EXT.test(file.name);
}

export async function preparseClientFile(file: File): Promise<ClientParseResult> {
  if (needsServerParse(file)) {
    return { kind: "needs_ai", reason: `Server-side parsing required for ${file.name}` };
  }

  const name = file.name.toLowerCase();
  const isXlsx = /\.(xlsx|xls)$/i.test(name) || /spreadsheet/i.test(file.type);
  const isCsv = /\.csv$/i.test(name) || file.type === "text/csv";

  if (isXlsx) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const sheetName = wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: "", raw: false });
    const headers = json.length ? Object.keys(json[0]) : [];
    const rows = json.map((r) => {
      const out: Record<string, string> = {};
      for (const h of headers) out[h] = String(r[h] ?? "").trim();
      return out;
    });
    return { kind: "table", headers, rows, sheet_name: sheetName };
  }

  if (isCsv) {
    const text = await file.text();
    const parsed = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
    const headers = parsed.meta.fields ?? [];
    const rows = (parsed.data ?? []).map((r) => {
      const out: Record<string, string> = {};
      for (const h of headers) out[h] = String(r[h] ?? "").trim();
      return out;
    });
    return { kind: "table", headers, rows };
  }

  // Plain text fallback
  const text = await file.text();
  return { kind: "text", text };
}

export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, "0")).join("");
}
